import { Component, Output, EventEmitter, Input, OnInit, inject } from '@angular/core';
import { FormBuilder, FormGroup, FormArray, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ProviderOffice } from 'app/core/models/provider-office.model';
import { ProviderOfficeFilters, ProviderService } from 'app/core/services/provider.service';
import { OutreachAttemptEvidence } from 'app/core/models/outreach-attempt-evidence.model';
import { keyedStatusOptions as keyedOfficeStatusOptions } from 'app/core/options/office-status.opts';
import { keyedStatusOptions as keyedProviderOfficeStatusOptions } from 'app/core/options/provider-office-status.opts';
import { ProviderOfficeStatus } from 'app/core/enums/provider-office-status.enum';
import { CaptureValidationService, ValidationKeys } from 'app/core/services/capture-validation.service';
import { Capture } from 'app/core/models/capture.model';
import { PatientAttachmentsService, OutreachAttemptEvidenceParams } from 'app/core/services/patient-attachments.service';
import { PatientAttachment } from 'app/core/models/patient-attachment.model';
import { sortBy } from 'lodash-es';

import { VerificationValidationComponent } from '../verification-validation/verification-validation.component';
import { OfficeComponent } from '../../../shared/components/office/office.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';

@Component({
    selector: 'app-outreach-attempt-evidence-form',
    templateUrl: './outreach-attempt-evidence-form.component.html',
    styleUrls: ['./outreach-attempt-evidence-form.component.scss'],
    imports: [FormsModule, ReactiveFormsModule, VerificationValidationComponent, OfficeComponent, MatProgressSpinner]
})
export class OutreachAttemptEvidenceFormComponent implements OnInit {
  private providerService = inject(ProviderService);
  private patientAttachmentsService = inject(PatientAttachmentsService);
  private formBuilder = inject(FormBuilder);
  private captureValidationService = inject(CaptureValidationService);

  @Input() capture: Capture;

  @Output() newPatientAttachment = new EventEmitter<PatientAttachment>();
  @Output() canceled = new EventEmitter<void>();

  loading = true;
  formValid = false;

  outreachAttemptEvidence = new OutreachAttemptEvidence();
  formGroup: FormGroup;
  providerOffices: ProviderOffice[] = [];

  keyedOfficeStatusOptions = keyedOfficeStatusOptions;
  keyedProviderOfficeStatusOptions = keyedProviderOfficeStatusOptions;

  validationKey = ValidationKeys.verifyingOutreachAttemptEvidence;
  verifiedProviderOfficeStatus = ProviderOfficeStatus.verified;

  onCancelClick() {
    this.canceled.emit();
  }

  onSubmit() {
    const params = this.prepareFormParams();

    params.patientPrescriberId = this.capture.patientPrescriberId;

    const validDate =
      this.captureValidationService.validateVerifyingOutreachAttemptEvidenceDate(
        this.capture,
        params.lastOutreachDate
      );

    if (validDate) {
      this.patientAttachmentsService.createOutreachAttemptEvidence(
        this.capture.patient.id,
        this.capture.prescriber.id,
        params
      ).
        subscribe((newPatientAttachment: PatientAttachment) => {
          this.newPatientAttachment.emit(newPatientAttachment);
        });
    }
  }

  ngOnInit() {
    const filters = new ProviderOfficeFilters();
    filters.withSentOutboundFaxesForPatientPrescriber = this.capture.patientPrescriberId;

    this.providerService.getProviderOffices(this.capture.provider.id, filters).
      subscribe(providerOffices => {
        this.providerOffices = this.sortProviderOffices(providerOffices);
        this.setupForm();
        this.loading = false;
      });
  }

  officeSelectedInArray(index: number) {
    const officesFormArray = this.formGroup.get('offices') as FormArray;
    return !!officesFormArray.get(index.toString())?.value;
  }

  private setupForm() {
    const officeOptions = this.providerOffices.map((providerOffice: ProviderOffice) => {
      return {
        value: false,
        disabled: providerOffice.status !== ProviderOfficeStatus.verified
      }
    });

    this.formGroup = this.formBuilder.group({
      lastOutreachDate: [],
      notes: [],
      offices: this.formBuilder.array(officeOptions)
    });

    this.formGroup.valueChanges.subscribe(() => {
      this.formValid = this.validateForm();
    });
  }

  private prepareFormParams(): OutreachAttemptEvidenceParams {
    const formValue = this.formGroup.value;

    const officeIds = [];
    this.providerOffices.forEach((providerOffice: ProviderOffice, index: number) => {
      if (this.officeSelectedInArray(index)) {
        officeIds.push(providerOffice.office.id);
      }
    });

    return {
      lastOutreachDate: formValue.lastOutreachDate,
      notes: formValue.notes,
      officeIds
    };
  }

  private validateForm() {
    const formValue = this.formGroup.value;

    return formValue.lastOutreachDate &&
      formValue.notes &&
      formValue.notes.length > 0 &&
      formValue.offices.some((v: boolean) => v);
  }

  private sortProviderOffices(providerOffices: ProviderOffice[]) {
    return sortBy(providerOffices, providerOffice => providerOffice.status !== ProviderOfficeStatus.verified);
  }
}
